'use client';
import { Dialog, DialogPanel } from '@headlessui/react';
import React, { useCallback, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';

import cn from '@/lib/clsxm';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import { VARIANT_ENUM } from '@/components/@base/button/type';
import Card, { CardAction, CardContent, CardHeader } from '@/components/@base/card';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Rating from '@/components/app/product/components/side-bar/rate-action/rating';
import { usePostRate } from '@/components/app/product/services/hooks';

import {
  DATA_CY_ADD_COMMENT_BOX,
  DATA_CY_ADD_COMMENT_TEXTAREA,
  DATA_CY_CLOSE_COMMENT_BOX,
  DATA_CY_SEND_COMMENT,
  DATA_CY_SHOW_COMMENT_TEXTAREA,
} from '@/constant/cypress';
import localization, { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function AddComment({ data, isShow, setShowModal }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [showError, setShowError] = useState(false);
  const [comment, setComment] = useState('');
  const [textAreaHeight, setTextAreaHeight] = useState('auto');

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);

    e.currentTarget.style.height = 'auto';
    const newHeight = Math.min(e.currentTarget.scrollHeight, 200);
    e.currentTarget.style.height = newHeight + 'px';

    setTextAreaHeight(newHeight + 'px');
  };
  const { mutate: postRateMutate, isPending } = usePostRate();

  function handleSelect(val: number) {
    setIsOpen(true);
    setSelectedRating(val);
  }

  const handleSave = useCallback(() => {
    if (selectedRating === 0) {
      showToast({ message: productLocalization.pleaseSelectARate, type: 'error' });
      return;
    }

    postRateMutate(
      { product_id: data.id, point: selectedRating, description: comment || '' },
      {
        onSuccess: () => {
          setShowModal(false);
          showToast({
            message: productLocalization.rateSavedSuccessfully,
            type: 'success',
          });
        },
        onError: (errors: any) => {
          setShowModal(false);
          const error =
            errors?.response?.data?.errors?.[0]?.message || localization.somethingWentWrong;
          showToast({
            message: error,
            type: 'error',
          });
        },
      },
    );
  }, [postRateMutate, comment, selectedRating]);

  return (
    <Dialog open={isShow} onClose={() => setShowModal(false)} className='relative z-100'>
      <div className='fixed inset-0 bg-black/50' onClick={(e) => e.preventDefault()} />
      <div data-cy={DATA_CY_ADD_COMMENT_BOX} className='fixed inset-0 overflow-y-auto'>
        <div className='flex min-h-full items-center justify-center text-center'>
          <DialogPanel className='w-full max-w-[370px]! space-y-4 overflow-hidden rounded-xl border bg-white p-0!'>
            <Container center className='relative flex-col p-0!'>
              <Card noBorder className='w-full px-1! py-1!'>
                <CardHeader className='flex justify-between rounded-xl bg-[linear-gradient(180deg,#6097f324_0%,rgba(96,151,243,0)_100%)] p-4'>
                  <Container>
                    <Text className='text-[15px] text-[#424244]' bold>
                      {localization.rateToYourPurchase}
                    </Text>
                    <Text size={SIZE_ENUM.XS} className='text-[#76767C]'>
                      {localization.yourRateIsUseful}
                    </Text>
                  </Container>

                  <Button
                    data-cy={DATA_CY_CLOSE_COMMENT_BOX}
                    variant={VARIANT_ENUM.SOUL}
                    className='group h-10 w-10 border-none bg-white p-0 shadow-none'
                    onClick={() => setShowModal(false)}
                  >
                    <AiOutlineClose
                      size={22}
                      className='text-gray-100 transition duration-300 group-hover:text-white'
                    />
                  </Button>
                </CardHeader>

                <CardContent>
                  <Container center className='justify-between'>
                    <Text size={SIZE_ENUM.SM} className='line-clamp-1 text-[#424244]'>
                      {data.title}
                    </Text>
                    <Button
                      data-cy={DATA_CY_SHOW_COMMENT_TEXTAREA}
                      onClick={() => setIsOpen(!isOpen)}
                      className='h-5 w-5 rounded-[4px] border border-[#EEF0F4] bg-white! p-1.5 hover:bg-gray-100'
                    >
                      <IoIosArrowDown
                        className={cn(
                          'text-[#878F9B] transition duration-300',
                          isOpen ? 'rotate-180' : 'rotate-0',
                        )}
                      />
                    </Button>
                  </Container>
                  <Container center className='justify-between gap-3 p-0'>
                    <Container center className='items-center justify-end gap-2'>
                      <XImage
                        className='rounded-full'
                        src={data?.icon?.publicLink}
                        alt={data?.icon?.title}
                        width={28}
                        height={28}
                      />
                      <Text className='line-clamp-1 text-[#878F9B]' size={SIZE_ENUM.XS}>
                        {data.storeName}
                      </Text>
                    </Container>
                    <Container center className='justify-start gap-1'>
                      <Text
                        className='mt-[6px] whitespace-nowrap text-[#878F9B]'
                        size={SIZE_ENUM.XS}
                      >
                        {localization.yourRate} :
                      </Text>
                      <Rating
                        onSelect={handleSelect}
                        totalStars={5}
                        size='text-2xl md:text-xl'
                      />
                    </Container>
                  </Container>

                  {isOpen && (
                    <Container className='mt-2! w-full overflow-hidden transition-all duration-300 ease-in-out'>
                      <textarea
                        data-cy={DATA_CY_ADD_COMMENT_TEXTAREA}
                        rows={4}
                        placeholder={productLocalization.addYourRateAndComment}
                        className={
                          'no-scrollbar! w-full resize-none rounded-md border p-2 text-[10px] text-[#424244] focus:outline-none ' +
                          (showError
                            ? 'border-red-500 focus:border-red-500'
                            : 'border-[#E6E9EF] focus:border-[#FFB616]')
                        }
                        value={comment || ''}
                        onChange={handleChange}
                        style={{ height: textAreaHeight, maxHeight: '200px' }}
                      />
                    </Container>
                  )}
                </CardContent>

                <CardAction className='flex items-center justify-end gap-2 pt-0'>
                  <Button
                    data-cy={DATA_CY_SEND_COMMENT}
                    data-submit-btn='true'
                    onClick={handleSave}
                    className='w-[84px] bg-[#6097F3] text-white hover:bg-[#F0F8FF] hover:text-[#6097F3]'
                    isLoading={isPending}
                  >
                    {localization.giveRate}
                  </Button>
                </CardAction>
              </Card>
            </Container>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
